import React from "react";

/**
 * Compact spinning prize wheel button for the Home top bar.
 * Continuously rotates slowly when the daily spin hasn't been claimed yet.
 * Stops + dims when already claimed.
 */
export default function MiniSpinWheel({ claimed = false, size = 44, onClick, testId = "home-spin-btn" }) {
  // 8 colorful pie slices matching the SPIN_REWARDS palette
  const colors = [
    "#4ade80", "#22d3ee", "#a78bfa", "#FBBF24",
    "#ef4444", "#ec4899", "#f97316", "#84cc16",
  ];
  const segCount = colors.length;
  const segAngle = 360 / segCount;

  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      title="Daily Spin"
      className="relative inline-flex items-center justify-center shrink-0 group"
      style={{ width: size, height: size }}
    >
      {/* Outer glow */}
      <span
        aria-hidden
        className="absolute inset-0 rounded-full pointer-events-none"
        style={{
          background: claimed
            ? "transparent"
            : "radial-gradient(circle, rgba(251,191,36,0.45) 0%, transparent 70%)",
          filter: "blur(6px)",
        }}
      />

      {/* Wheel */}
      <div
        className="relative rounded-full border-2 overflow-hidden"
        style={{
          width: size - 8,
          height: size - 8,
          borderColor: claimed ? "#1B364A" : "#FBBF24",
          boxShadow: claimed
            ? "0 2px 6px rgba(0,0,0,0.4)"
            : "0 6px 16px -4px rgba(251,191,36,0.55), inset 0 1px 0 rgba(255,255,255,0.2)",
          background: "#0E1F2A",
        }}
      >
        <div
          className="absolute inset-0"
          style={{
            animation: claimed ? "none" : "ps-wheel-spin 6s linear infinite",
            filter: claimed ? "saturate(0.25) brightness(0.55)" : "none",
          }}
        >
          {colors.map((c, i) => (
            <span
              key={c}
              className="absolute top-0 left-1/2 origin-bottom-left"
              style={{
                width: "50%",
                height: "50%",
                transform: `rotate(${i * segAngle}deg) skewY(${90 - segAngle}deg)`,
                background: `linear-gradient(135deg, ${c} 0%, ${c}aa 100%)`,
              }}
            />
          ))}
          {/* Center hub */}
          <span
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#0E1F2A] border"
            style={{
              width: size * 0.28,
              height: size * 0.28,
              borderColor: claimed ? "#1B364A" : "#FBBF24",
            }}
          />
        </div>
      </div>

      {/* Pointer arrow on top */}
      <span
        aria-hidden
        className="absolute top-0 left-1/2 -translate-x-1/2 z-10"
        style={{
          width: 0,
          height: 0,
          borderLeft: "5px solid transparent",
          borderRight: "5px solid transparent",
          borderTop: `8px solid ${claimed ? "#64748b" : "#FBBF24"}`,
          filter: `drop-shadow(0 1px 2px rgba(0,0,0,0.5))`,
        }}
      />

      {/* Unclaimed pulse */}
      {!claimed && (
        <span
          aria-hidden
          className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full ring-2 ring-[#050E15] animate-pulse"
          style={{ background: "#ef4444", boxShadow: "0 0 8px #ef4444" }}
        />
      )}
    </button>
  );
}
